import React, { useState, useCallback, useEffect } from 'react'
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb"
import { useSchoolAddGrade, useUserInfo, useNationalExams } from '../../react-query/queries'
import { useAuth } from '../../utils/Auth/AuthProvider'
import { Button as FlowButton } from 'flowbite-react'
import ExamProgressStepper from '../../components/Exams/ExamProgressStepper'
import ExamQuestionAddCard from '../../components/Exams/ExamQuestionAddCard'
import ExamQuestionsMeta from '../../components/Exams/ExamQuestionsMeta'
import { useSchoolExamQuestionAdd } from '../../react-query/queries'
import { adminGetInstitutionFields, adminGetFieldAcademicYears, adminGetAcademicYearCourses, AdminField, AdminAcademicYear } from '../../utils/remote';

const MixedExam: React.FC = () => {
    const { token } = useAuth()

    const [step, setStep] = useState<number>(1)
    const [gradeStateId, setGradeStateId] = useState<string>(() => { try { return localStorage.getItem('add_exam_grade') || '-1' } catch { return '-1' } })
    const [subjectStateId, setSubjectStateId] = useState<string>(() => { try { return localStorage.getItem('add_exam_subject') || '-1' } catch { return '-1' } })
    // For university flow allow multiple course selection
    const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>(() => { try { const raw = localStorage.getItem('add_exam_subjects'); return raw ? JSON.parse(raw) as string[] : []; } catch { return []; } })
    const [isAdding, setISAdding] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const [createdExamId, setCreatedExamId] = useState<string | null>(null)
    const [pendingQuestions, setPendingQuestions] = useState<any[] | null>(null)
    const [questionsExtracted, setQuestionsExtracted] = useState<boolean>(false)
    const [isExtractingQuestions, setIsExtractingQuestions] = useState<boolean>(false)
    const [childSaveFn, setChildSaveFn] = useState<(() => void) | null>(null)
    const [extractQuestionsFn, setExtractQuestionsFn] = useState<(() => void) | null>(null)
    const { mutateAsync: schoolExamQuestionAdd } = useSchoolExamQuestionAdd()

    // schoolAddExam is used for creating the exam payload regardless of school or university flow
    const { mutateAsync: schoolAddExam } = useSchoolAddGrade()

    const memoizedOnProceedToMeta = useCallback((qs: any[]) => { setPendingQuestions(qs); setQuestionsExtracted(true); }, [setPendingQuestions, setQuestionsExtracted]);
    const memoizedOnRegisterExtractFn = useCallback((fn: () => void) => setExtractQuestionsFn(() => fn), [setExtractQuestionsFn]);
    const memoizedOnQuestionsExtracted = useCallback(() => setQuestionsExtracted(true), [setQuestionsExtracted]);

    const [formdata, setFormdata] = useState({ name: '', given_time: 60, description: '' })
    const [isNational, setIsNational] = useState<boolean>(false)
    const [selectedNationalExam, setSelectedNationalExam] = useState<string>('-1')

    const { role } = useAuth()

    const { data: userData } = useUserInfo()

    // University flow helpers: fields, academic years and courses
    const [fieldsData, setFieldsData] = useState<AdminField[] | null>(null);
    const [academicYears, setAcademicYears] = useState<AdminAcademicYear[] | null>(null);
    const [academicYearId, setAcademicYearId] = useState<string>('-1');
    const [coursesData, setCoursesData] = useState<{ id: number; name: string }[] | null>(null);

    const targetLevel: 'school' | 'college_university' | undefined = ((): any => {
        const raw = (userData?.institution_type ?? userData?.institution_name ?? '') as string
        const instType = String(raw || '').trim().toLowerCase()
        if (instType.includes('school')) return 'school'
        if (instType.includes('college') || instType.includes('university') || instType.includes('college_university')) return 'college_university'
        const r = String(role ?? '').toLowerCase()
        if (r === 'school') return 'school'
        if (r.includes('college') || r.includes('university') || r === 'college_university') return 'college_university'
        return undefined
    })()

    const { status: nationalStatus, data: nationalData } = useNationalExams(targetLevel, true)

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target
        setFormdata((prev) => ({ ...prev, [name]: name === 'given_time' ? Number(value) : value }))
    }

    // For school flow grade->subject, for university flow gradeStateId maps to field, subjectStateId maps to course
    const isValidStep1 = () => gradeStateId !== '-1'
    const isValidStep2 = () => {
        if (targetLevel === 'college_university') return selectedCourseIds && selectedCourseIds.length > 0
        return subjectStateId !== '-1'
    }
    const isValidStep3 = () => formdata.name.trim().length >= 3 && Number(formdata.given_time) > 0 && Number(formdata.given_time) <= 300

    const [createdExamIds, setCreatedExamIds] = useState<string[] | null>(null)

    const createExam = async (e?: React.FormEvent) => {
        if (e) e.preventDefault()
        if (isAdding) return
        setSubmitError(null)
        if (!isValidStep3()) { setSubmitError('Please complete all fields correctly before continuing.'); return }
        if (isNational) {
            if (!selectedNationalExam || selectedNationalExam === '-1') {
                setSubmitError('Please choose a national exam or turn off the National Exam option before continuing.')
                return
            }
        }

        setISAdding(true)
        try {
            const created: string[] = []

            // Determine course ids to create exams for
            const targetCourseIds = targetLevel === 'college_university' ? (selectedCourseIds || []) : (subjectStateId && subjectStateId !== '-1' ? [subjectStateId] : [])
            if (!targetCourseIds || targetCourseIds.length === 0) {
                setISAdding(false)
                setSubmitError('Please select at least one course/subject.')
                return
            }

            // If university flow, create a single exam and attach all selected courses via the `courses` payload
            if (targetLevel === 'college_university') {
                const postData = new FormData()
                postData.append('name', formdata.name.trim())
                postData.append('time', `${formdata.given_time}`)
                postData.append('desc', formdata.description?.trim() || '')
                // Do NOT append grade_id for university flow (gradeStateId holds FieldOfStudy id there)
                // only append grade when using school flow (handled in the other branch)
                // send courses as JSON string
                postData.append('courses', JSON.stringify(targetCourseIds))
                if (isNational) {
                    postData.append('exam_scope', 'national')
                    if (selectedNationalExam && selectedNationalExam !== '-1') postData.append('national_exam', selectedNationalExam)
                } else {
                    postData.append('exam_scope', 'none')
                }
                try {
                    const resp: any = await schoolAddExam({ toBeValidated: token, data: postData } as any)
                    if (resp && (resp['exam_id'] || resp['done'] === 'done')) {
                        const id = resp['exam_id'] || (resp && resp.id)
                        if (id) created.push(String(id))
                    } else {
                        console.warn('Unexpected create exam response', resp)
                    }
                } catch (err) {
                    console.error('Failed to create exam for courses', targetCourseIds, err)
                }
            } else {
                for (const cid of targetCourseIds) {
                    const postData = new FormData()
                    postData.append('name', formdata.name.trim())
                    postData.append('time', `${formdata.given_time}`)
                    postData.append('desc', formdata.description?.trim() || '')
                    // Only append grade/subject when valid selections exist
                    if (gradeStateId && gradeStateId !== '-1') postData.append('grade_id', gradeStateId)
                    if (cid && String(cid) !== '-1') postData.append('subject_id', cid)
                    if (isNational) {
                        postData.append('exam_scope', 'national')
                        if (selectedNationalExam && selectedNationalExam !== '-1') postData.append('national_exam', selectedNationalExam)
                    } else {
                        postData.append('exam_scope', 'none')
                    }

                    try {
                        const resp: any = await schoolAddExam({ toBeValidated: token, data: postData } as any)
                        // submitSchoolAddExam returns {done: 'done', exam_id}
                        if (resp && (resp['exam_id'] || resp['done'] === 'done')) {
                            const id = resp['exam_id'] || (resp && resp.id)
                            if (id) created.push(String(id))
                        } else {
                            console.warn('Unexpected create exam response', resp)
                        }
                    } catch (err) {
                        console.error('Failed to create exam for course', cid, err)
                    }
                }
            }

            setISAdding(false)
            if (created.length > 0) {
                try { localStorage.setItem('add_exam_grade', gradeStateId); localStorage.setItem('add_exam_subjects', JSON.stringify(targetCourseIds)) } catch {}
                setCreatedExamIds(created)
                setCreatedExamId(created[0] || null)
                setStep(4)
            } else {
                setSubmitError('Failed to create any exams. Please try again.')
            }
        } catch (err) {
            console.error('createExam error', err)
            setISAdding(false)
            setSubmitError('Failed to create exam. Please try again.')
        }
    }

    // Load university fields for institution
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const instId = userData?.institution_id ?? null;
                if (targetLevel === 'college_university' && instId) {
                    const fields = await adminGetInstitutionFields(instId);
                    if (!mounted) return;
                    setFieldsData(fields || []);
                    if ((fields || []).length && gradeStateId === '-1') {
                        setGradeStateId(String((fields[0] && fields[0].id) || '-1'))
                    }
                } else {
                    if (mounted) setFieldsData(null);
                }
            } catch (e) {
                console.error('Failed to load institution fields', e);
                if (mounted) setFieldsData([]);
            }
        })();
        return () => { mounted = false; };
    }, [targetLevel, userData, gradeStateId]);

    // When a field is selected, load academic years
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const instId = userData?.institution_id ?? null;
                if (targetLevel === 'college_university' && instId && gradeStateId && gradeStateId !== '-1') {
                    const years = await adminGetFieldAcademicYears(instId, Number(gradeStateId));
                    if (!mounted) return;
                    setAcademicYears(years || []);
                    setAcademicYearId('-1');
                    setCoursesData(null);
                    setSubjectStateId('-1');
                } else {
                    if (mounted) setAcademicYears(null);
                }
            } catch (e) {
                console.error('Failed to load academic years', e);
                if (mounted) setAcademicYears([]);
            }
        })();
        return () => { mounted = false; };
    }, [targetLevel, userData, gradeStateId]);

    // When an academic year is selected, load courses
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const instId = userData?.institution_id ?? null;
                if (targetLevel === 'college_university' && instId && gradeStateId && gradeStateId !== '-1' && academicYearId && academicYearId !== '-1') {
                    const courses = await adminGetAcademicYearCourses(instId, Number(gradeStateId), Number(academicYearId));
                    if (!mounted) return;
                    setCoursesData(courses || []);
                    setSubjectStateId('-1');
                } else {
                    if (mounted) setCoursesData(null);
                }
            } catch (e) {
                console.error('Failed to load courses for academic year', e);
                if (mounted) setCoursesData([]);
            }
        })();
        return () => { mounted = false; };
    }, [targetLevel, userData, gradeStateId, academicYearId]);

    // University render helpers
    const renderFieldTile = (field: AdminField) => {
        const selected = String(field.id) === gradeStateId
        return (
            <button key={field.id} type="button" onClick={() => { setGradeStateId(String(field.id)); setAcademicYearId('-1'); setSubjectStateId('-1'); setCoursesData(null); }} className={`rounded-lg border p-4 text-left transition ${selected ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/40' : 'border-stroke hover:border-primary/60 hover:bg-primary/5'}`}>
                <div className="text-sm text-bodydark2">Field</div>
                <div className="text-lg font-semibold">{field.name}</div>
            </button>
        )
    }

    const renderAcademicYearTile = (year: AdminAcademicYear) => {
        const selected = String(year.id) === academicYearId
        return (
            <button key={year.id} type="button" onClick={() => { setAcademicYearId(String(year.id)); setSubjectStateId('-1'); setCoursesData(null); }} className={`rounded-lg border p-4 text-left transition ${selected ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/40' : 'border-stroke hover:border-primary/60 hover:bg-primary/5'}`}>
                <div className="text-sm text-bodydark2">Academic Year</div>
                <div className="text-lg font-semibold">{year.name}</div>
            </button>
        )
    }

    const renderCourseTile = (course: { id: number; name: string }) => {
        const cid = String(course.id)
        const selectedMulti = selectedCourseIds && selectedCourseIds.includes(cid)
        const selectedSingle = String(course.id) === subjectStateId
        const selected = targetLevel === 'college_university' ? selectedMulti : selectedSingle
        return (
            <button key={course.id} type="button" onClick={() => {
                if (targetLevel === 'college_university') {
                    // toggle in selectedCourseIds
                    setSelectedCourseIds(prev => {
                        const cur = Array.isArray(prev) ? [...prev] : []
                        const exists = cur.includes(cid)
                        const next = exists ? cur.filter(x => x !== cid) : [...cur, cid]
                        try { localStorage.setItem('add_exam_subjects', JSON.stringify(next)) } catch {}
                        // also keep subjectStateId in sync with last selection for compatibility
                        if (next.length > 0) setSubjectStateId(next[0])
                        else setSubjectStateId('-1')
                        return next
                    })
                } else {
                    setSubjectStateId(String(course.id))
                }
            }} className={`rounded-lg border p-4 text-left transition ${selected ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/40' : 'border-stroke hover:border-primary/60 hover:bg-primary/5'}`}>
                <div className="text-lg font-semibold">{course.name}</div>
            </button>
        )
    }

    return (
        <div className="col-span-12 rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5">
            <div className="max-w-full overflow-x-auto">
                <h4 className="mb-4 text-xl font-semibold text-black">Create a new exam</h4>
            </div>
            <ExamProgressStepper current={step} />

            <div className="mt-4">
                {step === 1 && (
                    <div>
                        <h2 className="pb-3 pt-1 text-base font-semibold">1. Choose Field of Study / Grade</h2>
                        {/* If university flow, show fields; otherwise show grades (legacy school flow) */}
                        {targetLevel === 'college_university' ? (
                            fieldsData ? (
                                <div className="grid gap-4 xsm:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                                    {fieldsData.map((f: AdminField) => renderFieldTile(f))}
                                </div>
                            ) : (
                                <div className="text-bodydark2">Loading fields…</div>
                            )
                        ) : (
                            // school flow falls back to original grade tiles; gracefully handle missing hook
                            <div className="text-bodydark2">Please use the school flow to create exams (grades/subjects).</div>
                        )}
                    </div>
                )}

                {step === 2 && (
                    <div>
                        <h2 className="pb-3 pt-1 text-base font-semibold">2. Choose Academic Year and Course</h2>
                        {targetLevel === 'college_university' ? (
                            <>
                                <div className="mb-4">
                                    <div className="text-sm text-bodydark2 mb-2">Academic years</div>
                                    {academicYears ? (
                                        <div className="grid gap-4 xsm:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                                            {academicYears.map((y: AdminAcademicYear) => renderAcademicYearTile(y))}
                                        </div>
                                    ) : (
                                        <div className="text-bodydark2">Select a field to load academic years…</div>
                                    )}
                                </div>

                                <div className="mt-6">
                                    <div className="text-sm text-bodydark2 mb-2">Courses</div>
                                    {coursesData ? (
                                        <div className="grid gap-4 xsm:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                                            {coursesData.map((c: any) => renderCourseTile(c))}
                                        </div>
                                    ) : (
                                        <div className="text-bodydark2">Select an academic year to load courses…</div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="text-bodydark2">Please use the school flow to create exams (grades/subjects).</div>
                        )}
                    </div>
                )}

                {step === 3 && (
                    <div>
                        <h2 className="pb-3 pt-1 text-base font-semibold">3. Exam Details</h2>
                        {submitError && <div className="mb-3 rounded border border-red-300 bg-red-50 p-2.5 text-red-700">{submitError}</div>}
                        <form onSubmit={createExam}>
                            <div className="p-6.5">
                                    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                                        <div>
                                            <label className="mb-2.5 block text-black">National Exam</label>
                                            <div className="flex items-center gap-3">
                                                <label className={`relative m-0 block h-7.5 w-14 rounded-full ${isNational ? 'bg-primary' : 'bg-stroke'}`}>
                                                    <input
                                                        type="checkbox"
                                                        aria-label="Mark as national exam"
                                                        checked={isNational}
                                                        onChange={() => { setIsNational(!isNational); if (!isNational) setSelectedNationalExam('-1') }}
                                                        className="dur absolute top-0 z-50 m-0 h-full w-full cursor-pointer opacity-0"
                                                    />
                                                    <span className={`absolute top-1/2 left-[3px] flex h-6 w-6 -translate-y-1/2 translate-x-0 items-center justify-center rounded-full bg-white shadow-switcher duration-75 ease-linear ${isNational && '!right-[3px] !translate-x-full'}`}>
                                                        <span className="dark:hidden">
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.99992 12.6666C10.5772 12.6666 12.6666 10.5772 12.6666 7.99992C12.6666 5.42259 10.5772 3.33325 7.99992 3.33325C5.42259 3.33325 3.33325 5.42259 3.33325 7.99992C3.33325 10.5772 5.42259 12.6666 7.99992 12.6666Z" fill="#969AA1"/></svg>
                                                        </span>
                                                        <span className="hidden dark:inline-block">
                                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.3533 10.62C14.2466 10.44 13.9466 10.16 13.1999 10.2933C12.7866 10.3667 12.3666 10.4 11.9466 10.38C10.3933 10.3133 8.98659 9.6 8.00659 8.5C7.13993 7.53333 6.60659 6.27333 6.59993 4.91333C6.59993 4.15333 6.74659 3.42 7.04659 2.72666C7.33993 2.05333 7.13326 1.7 6.98659 1.55333C6.83326 1.4 6.47326 1.18666 5.76659 1.48C3.03993 2.62666 1.35326 5.36 1.55326 8.28666C1.75326 11.04 3.68659 13.3933 6.24659 14.28C6.85993 14.4933 7.50659 14.62 8.17326 14.6467C8.27993 14.6533 8.38659 14.66 8.49326 14.66C10.7266 14.66 12.8199 13.6067 14.1399 11.8133C14.5866 11.1933 14.4666 10.8 14.3533 10.62Z" fill="#969AA1"/></svg>
                                                        </span>
                                                    </span>
                                                </label>
                                                <div className="text-sm">Mark as national exam</div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="mb-2.5 block text-black">Choose National Exam</label>
                                            <select aria-label="National exam select" disabled={!isNational || nationalStatus !== 'success'} value={selectedNationalExam} onChange={(e) => setSelectedNationalExam(e.target.value)} className={`w-full rounded border-[1.5px] border-stroke py-3 px-5 ${!isNational ? 'bg-gray-50 text-bodydark2' : 'bg-white'}`}>
                                                <option value="-1">-- Select national exam --</option>
                                                {nationalStatus === 'success' && (nationalData || []).map((n: any) => (
                                                    <option key={n.id} value={String(n.id)}>{n.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                <div className="mb-4.5 flex flex-col gap-6 xl:flex-row">
                                    <div className="w-full xl:w-1/2">
                                        <label className="mb-2.5 block text-black">Exam Name</label>
                                        <input type="text" placeholder="e.g. Midterm Assessment" name="name" value={formdata.name} onChange={handleInputChange} className="w-full rounded border-[1.5px] border-stroke py-3 px-5" />
                                    </div>
                                    <div className="w-full xl:w-1/2">
                                        <label className="mb-2.5 block text-black">Time (minutes)</label>
                                        <input type="number" min={1} max={300} placeholder="60" name="given_time" value={formdata.given_time} onChange={handleInputChange} className="w-full rounded border-[1.5px] border-stroke py-3 px-5" />
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <label className="mb-2.5 block text-black">Description</label>
                                    <textarea rows={5} placeholder="Add an optional description" name="description" value={formdata.description} onChange={handleInputChange} className="w-full rounded border-[1.5px] border-stroke py-3 px-5" />
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {step === 4 && createdExamId && (
                    <div>
                        <h2 className="pb-3 pt-1 text-base font-semibold">4. Add Questions</h2>
                        <div className="mt-4">
                            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                            {/* @ts-ignore */}
                            <ExamQuestionAddCard
                                examId={createdExamId}
                                onProceedToMeta={memoizedOnProceedToMeta}
                                onRegisterExtractFn={memoizedOnRegisterExtractFn}
                                onQuestionsExtracted={memoizedOnQuestionsExtracted}
                                isExtracting={isExtractingQuestions}
                                setIsExtracting={setIsExtractingQuestions}
                            />
                        </div>
                    </div>
                )}

                {step === 5 && pendingQuestions && ( (createdExamIds && createdExamIds.length > 0) || createdExamId ) && (
                    <div>
                        {/* Use first created exam id for the UI components but save questions to all created exams when university users selected multiple courses */}
                        <ExamQuestionsMeta
                            subjectId={subjectStateId}
                            gradeId={gradeStateId}
                            examId={(createdExamIds && createdExamIds[0]) || createdExamId}
                            questions={pendingQuestions}
                            onBack={() => setStep(4)}
                            onSave={async (qs) => {
                                try {
                                    const examIdsToSave = (createdExamIds && createdExamIds.length > 0) ? createdExamIds : (createdExamId ? [createdExamId] : [])
                                    const results: any[] = []
                                    for (const exId of examIdsToSave) {
                                        try {
                                            const resp = await schoolExamQuestionAdd({ toBeValidated: token, data: qs as any, exam_id: exId })
                                            results.push({ exam: exId, resp })
                                        } catch (err) {
                                            console.error('Failed to save questions for exam', exId, err)
                                            results.push({ exam: exId, error: err })
                                        }
                                    }
                                    return results
                                } catch (err) {
                                    console.error('Failed to save questions', err)
                                    throw err
                                }
                            }}
                            onRegisterSave={(fn: () => void) => setChildSaveFn(() => fn)}
                        />
                    </div>
                )}
            </div>

            <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-50 flex items-center gap-3">
                {step === 1 && (
                    <FlowButton size="lg" onClick={() => { if (!isValidStep1()) { setSubmitError(targetLevel === 'college_university' ? 'Please select a field of study to continue.' : 'Please select a grade to continue.'); return } setSubmitError(null); setStep(2) }}>
                        Next
                    </FlowButton>
                )}

                {step === 2 && (
                    <>
                        <button className="px-4 py-2 border rounded bg-white text-gray-700 border-gray-300 hover:bg-gray-100" onClick={() => setStep(1)}>Back</button>
                        <FlowButton size="lg" onClick={() => { if (!isValidStep2()) { setSubmitError(targetLevel === 'college_university' ? 'Please select a course to continue.' : 'Please select a subject to continue.'); return } setSubmitError(null); setStep(3) }}>Next</FlowButton>
                    </>
                )}

                {step === 3 && (
                    <>
                        <button className="px-4 py-2 border rounded bg-white text-gray-700 border-gray-300 hover:bg-gray-100" onClick={() => setStep(2)}>Back</button>
                        <FlowButton size="lg" onClick={() => { if (!isAdding) createExam(); }} disabled={!isValidStep3() || isAdding}>{isAdding ? 'Creating…' : 'Create Exam & Next'}</FlowButton>
                    </>
                )}

                {step === 4 && (
                    <>
                        <button className="px-4 py-2 border rounded bg-white text-gray-700 border-gray-300 hover:bg-gray-100" onClick={() => setStep(3)}>Back</button>
                        {!questionsExtracted ? (
                            <FlowButton size="lg" onClick={() => { if (extractQuestionsFn) { extractQuestionsFn(); } }} disabled={isExtractingQuestions}>
                                {isExtractingQuestions ? 'Extracting...' : 'Extract Questions'}
                            </FlowButton>
                        ) : (
                            <FlowButton size="lg" onClick={() => { if (pendingQuestions) { setStep(5); } }}>
                                Next: Chapter and Explanation
                            </FlowButton>
                        )}
                    </>
                )}

                {step === 5 && (
                    <>
                        <button className="px-4 py-2 border rounded bg-white text-gray-700 border-gray-300 hover:bg-gray-100" onClick={() => setStep(4)}>Back</button>
                        <FlowButton size="lg" onClick={() => { if (childSaveFn) childSaveFn(); else alert('Save handler not registered yet. Use the Save button in the page.') }}>Save</FlowButton>
                    </>
                )}
            </div>
        </div>
    )
}

const MixedExamPage: React.FC = () => {
    return (
        <>
            <Breadcrumb pageName="Mixed Exam" />
            <div className="grid grid-cols-12 gap-4">
                <MixedExam />
            </div>
        </>
    )
}

export default MixedExamPage
